import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, CheckCircle2, Copy, Eye, FileCode2, Inbox, Library, LogOut, Mail, Package, Save, Settings, ShoppingBag, TrendingUp } from "lucide-react";
import { adminApi, API } from "../../api";
import { toast } from "sonner";

const TABS = [
  { id: "przeglad", label: "Przegląd", icon: BarChart3 },
  { id: "zamowienia", label: "Zamówienia", icon: ShoppingBag },
  { id: "bestsellery", label: "Bestsellery", icon: TrendingUp },
  { id: "ruch", label: "Źródła ruchu", icon: Eye },
  { id: "feedy", label: "Feedy XML", icon: FileCode2 },
  { id: "zapytania", label: "Zapytania", icon: Inbox },
  { id: "ustawienia", label: "Ustawienia", icon: Settings },
];

const STATUS_COLORS = {
  "nowe": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "oplacone": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "w-realizacji": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "zrealizowane": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "anulowane": "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const STATUSES = Object.keys(STATUS_COLORS);
const PIE_COLORS = ["#06b6d4", "#3b82f6", "#14b8a6", "#8b5cf6", "#f59e0b", "#64748b"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("przeglad");
  const [overview, setOverview] = useState(null);
  const [orders, setOrders] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [bestsellers, setBestsellers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);

  useEffect(() => {
    adminApi().get("/auth/me")
      .then(() => setReady(true))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (!ready) return;
    const http = adminApi();
    http.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    http.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    http.get("/admin/traffic").then((r) => setTraffic(r.data)).catch(() => {});
    http.get("/admin/bestsellers").then((r) => setBestsellers(r.data)).catch(() => {});
    http.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    http.get("/admin/book-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, [ready]);

  const logout = () => { localStorage.removeItem("dl_admin_token"); navigate("/admin/login"); };

  const changeStatus = async (orderId, status) => {
    try {
      await adminApi().patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status, ...(status === "oplacone" ? { payment_status: "paid", delivered_at: o.delivered_at || new Date().toISOString() } : {}) } : o)));
      toast.success(status === "oplacone" ? `Odhaczono ${orderId} jako opłacone — produkt wysłany e-mailem` : `Zaktualizowano status zamówienia ${orderId}`);
    } catch {
      toast.error("Nie udało się zmienić statusu");
    }
  };

  const previewEmail = async (orderId) => {
    try {
      const r = await adminApi().get(`/admin/orders/${orderId}/email-preview`);
      setEmailPreview(r.data.html);
    } catch {
      toast.error("Nie udało się wygenerować podglądu e-maila");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await adminApi().put("/admin/settings", {
        company_name: settings.company_name || "", company_address: settings.company_address || "",
        nip: settings.nip || "", bank_account: settings.bank_account || "",
        blik_phone: settings.blik_phone || "", contact_email: settings.contact_email || "",
      });
      toast.success("Ustawienia zapisane");
    } catch {
      toast.error("Nie udało się zapisać ustawień");
    } finally {
      setSaving(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Skopiowano do schowka"));
  };

  if (!ready) return <main className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-500">Weryfikacja...</main>;

  return (
    <main className="min-h-screen bg-slate-950" data-testid="admin-panel">
      <div className="border-b border-cyan-500/10 bg-slate-900/50 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
              <Library className="h-4 w-4 text-white" />
            </div>
            <span className="font-display text-lg font-extrabold"><span className="gradient-text">DigiLib</span><span className="text-slate-400"> Admin</span></span>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full glass-panel px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-rose-300">
            <LogOut className="h-3.5 w-3.5" /> Wyloguj
          </button>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 pb-3 sm:px-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === t.id ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950" : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {tab === "przeglad" && overview && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { label: "Przychód (opłacone)", value: `${overview.revenue.toLocaleString("pl-PL")} zł` },
                { label: "Zamówienia", value: overview.orders_total },
                { label: "Wejścia na stronę", value: overview.views_total.toLocaleString("pl-PL") },
                { label: "Śr. wartość koszyka", value: `${overview.avg_order} zł` },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl glass-panel p-5">
                  <div className="text-xs text-slate-400">{s.label}</div>
                  <div className="mt-2 font-display text-2xl font-extrabold text-white">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <div className="rounded-2xl glass-panel p-5 lg:col-span-2">
                <h3 className="font-display text-sm font-bold text-slate-200">Przychód dzienny (opłacone zamówienia)</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview.sales_chart}>
                      <defs>
                        <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #164e63", borderRadius: 12, fontSize: 12 }} />
                      <Area type="monotone" dataKey="revenue" stroke="#06b6d4" fill="url(#rev)" strokeWidth={2.5} name="Przychód (zł)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-2xl glass-panel p-5">
                <h3 className="font-display text-sm font-bold text-slate-200">Zamówienia wg statusu</h3>
                <div className="mt-4 space-y-3">
                  {STATUSES.map((s) => (
                    <div key={s} className="flex items-center justify-between">
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_COLORS[s]}`}>{s}</span>
                      <span className="font-mono text-sm font-bold text-slate-200">{overview.orders_by_status[s] || 0}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-xl bg-slate-900/60 p-4 text-xs text-slate-400">
                  Wejścia dzisiaj: <span className="font-bold text-cyan-300">{overview.views_today}</span> · Produkty w katalogu: <span className="font-bold text-cyan-300">{overview.products}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "zamowienia" && (
          <div className="rounded-2xl glass-panel p-5" data-testid="admin-orders">
            <h3 className="font-display text-sm font-bold text-slate-200">Wszystkie zamówienia ({orders.length})</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="pb-3 pr-4 font-semibold">Numer</th>
                    <th className="pb-3 pr-4 font-semibold">Data</th>
                    <th className="pb-3 pr-4 font-semibold">Klient</th>
                    <th className="pb-3 pr-4 font-semibold">Produkty</th>
                    <th className="pb-3 pr-4 font-semibold">Kwota</th>
                    <th className="pb-3 pr-4 font-semibold">Metoda</th>
                    <th className="pb-3 pr-4 font-semibold">Status</th>
                    <th className="pb-3 font-semibold">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-800/60 text-slate-300" data-testid={`admin-order-${o.id}`}>
                      <td className="py-3 pr-4 font-mono font-bold text-cyan-300">{o.id}</td>
                      <td className="py-3 pr-4">{o.created_at.slice(0, 10)}</td>
                      <td className="py-3 pr-4">{o.email || "—"}</td>
                      <td className="max-w-[240px] py-3 pr-4"><span className="line-clamp-1">{o.items.map((i) => i.title).join(", ")}</span></td>
                      <td className="py-3 pr-4 font-bold">{o.total.toFixed(2)} zł</td>
                      <td className="py-3 pr-4">
                        <span className="text-[11px] font-semibold text-slate-400">{o.payment_method === "transfer" ? "Przelew" : "Stripe"}</span>
                        {o.delivered_at && <span className="ml-1.5 rounded-full bg-teal-500/15 px-2 py-0.5 text-[10px] font-bold text-teal-300">wysłano</span>}
                      </td>
                      <td className="py-3 pr-4">
                        <select
                          data-testid={`order-status-${o.id}`}
                          value={o.status}
                          onChange={(e) => changeStatus(o.id, e.target.value)}
                          className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 outline-none focus:border-cyan-500"
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1.5">
                          {o.payment_method === "transfer" && o.payment_status !== "paid" && (
                            <button
                              data-testid={`order-mark-paid-${o.id}`}
                              onClick={() => changeStatus(o.id, "oplacone")}
                              title="Odhacz jako opłacone — produkt zostanie wysłany e-mailem"
                              className="flex items-center gap-1 rounded-lg bg-emerald-500/15 border border-emerald-500/40 px-2.5 py-1.5 text-[11px] font-bold text-emerald-300 transition-colors hover:bg-emerald-500/25"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Opłacono
                            </button>
                          )}
                          <button
                            data-testid={`order-email-preview-${o.id}`}
                            onClick={() => previewEmail(o.id)}
                            title="Podgląd e-maila z produktem"
                            className="flex items-center gap-1 rounded-lg bg-cyan-500/10 border border-cyan-500/30 px-2.5 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20"
                          >
                            <Mail className="h-3.5 w-3.5" /> E-mail
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "bestsellery" && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-bestsellers">
            <div className="rounded-2xl glass-panel p-5">
              <h3 className="font-display text-sm font-bold text-slate-200">Najlepiej sprzedające się produkty</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bestsellers.slice(0, 8)} layout="vertical">
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} />
                    <YAxis type="category" dataKey="title" width={150} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #164e63", borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="sold" fill="#06b6d4" radius={[0, 6, 6, 0]} name="Sprzedane sztuki" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl glass-panel p-5">
              <h3 className="font-display text-sm font-bold text-slate-200">Ranking</h3>
              <div className="mt-4 space-y-2.5">
                {bestsellers.map((b, i) => (
                  <div key={b.product_id} className="flex items-center gap-3 rounded-xl bg-slate-900/50 p-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 font-display text-xs font-extrabold text-slate-950">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-200">{b.title}</div>
                      <div className="text-[11px] text-slate-500">{b.sold} szt. · {b.revenue.toFixed(2)} zł przychodu</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "ruch" && traffic && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-traffic">
            <div className="rounded-2xl glass-panel p-5">
              <h3 className="font-display text-sm font-bold text-slate-200">Źródła wejść na stronę</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={traffic.sources} dataKey="views" nameKey="source" innerRadius={60} outerRadius={100} paddingAngle={3}>
                      {traffic.sources.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #164e63", borderRadius: 12, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {traffic.sources.map((s, i) => (
                  <span key={s.source} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {s.source} ({s.views.toLocaleString("pl-PL")})
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl glass-panel p-5">
              <h3 className="font-display text-sm font-bold text-slate-200">Wejścia dzienne (ostatnie 14 dni)</h3>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={traffic.daily}>
                    <defs>
                      <linearGradient id="views" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #164e63", borderRadius: 12, fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" stroke="#14b8a6" fill="url(#views)" strokeWidth={2.5} name="Wejścia" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {tab === "feedy" && (
          <div className="grid gap-4 md:grid-cols-3" data-testid="admin-feeds">
            {[
              { name: "Google Merchant Center", desc: "Standardowy plik RSS 2.0 zgodny ze specyfikacją Google Merchant (g:id, g:price, g:gtin).", url: `${BACKEND}/api/feeds/google.xml` },
              { name: "Facebook / Meta Ads", desc: "Katalog produktowy dla Facebook Commerce i Instagram Shopping (format zgodny z Google).", url: `${BACKEND}/api/feeds/facebook.xml` },
              { name: "Ceneo / Skąpiec", desc: "Format ofert Ceneo (offers/o) akceptowany także przez Skąpiec i inne polskie porównywarki.", url: `${BACKEND}/api/feeds/ceneo.xml` },
            ].map((f) => (
              <div key={f.name} className="rounded-2xl glass-panel p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-400">
                  <FileCode2 className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-sm font-bold text-slate-100">{f.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{f.desc}</p>
                <div className="mt-4 flex items-center gap-2">
                  <input readOnly value={f.url} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-[10px] text-slate-400 outline-none" />
                  <button onClick={() => copy(f.url)} data-testid={`feed-copy-${f.name.toLowerCase().replace(/[\s/]+/g, "-")}`} className="shrink-0 rounded-lg bg-cyan-500/15 p-2 text-cyan-300 transition-colors hover:bg-cyan-500/25" aria-label="Kopiuj URL">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <a href={f.url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[11px] font-semibold text-cyan-400 hover:underline">Otwórz feed XML</a>
              </div>
            ))}
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 md:col-span-3">
              <p className="text-xs leading-relaxed text-amber-200/80">
                Wskazówka: wklej powyższe adresy URL jako źródło katalogu w Google Merchant Center, Meta Commerce Manager oraz panelach Ceneo/Skąpiec. Feedy generują się na żywo z aktualnego katalogu produktów.
              </p>
            </div>
          </div>
        )}

        {tab === "zapytania" && (
          <div className="rounded-2xl glass-panel p-5" data-testid="admin-book-requests">
            <h3 className="font-display text-sm font-bold text-slate-200">Zgłoszenia „Nie znalazłem książki" ({requests.length})</h3>
            {requests.length === 0 && <p className="mt-4 text-xs text-slate-500">Brak zgłoszeń — pojawią się tutaj, gdy klient wyśle formularz.</p>}
            <div className="mt-4 space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl bg-slate-900/50 p-4" data-testid={`book-request-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="font-mono text-xs font-bold text-fuchsia-300">{r.id}</span>
                    <span className="rounded-full border border-slate-700 px-2.5 py-0.5 text-[10px] font-semibold text-slate-300">{r.format}</span>
                    <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-sky-300">{r.status}</span>
                    <span className="ml-auto text-[10px] text-slate-500">{r.created_at.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-100">{r.title || "— (bez tytułu)"}</div>
                  {r.description && <p className="mt-1 text-xs leading-relaxed text-slate-400">{r.description}</p>}
                  <div className="mt-2 text-xs text-cyan-300">{r.email}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "ustawienia" && settings && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-settings">
            <div className="rounded-2xl glass-panel p-6">
              <h3 className="font-display text-sm font-bold text-slate-200">Dane firmy (widoczne w stopce i dokumentach prawnych)</h3>
              <div className="mt-5 space-y-4">
                {[
                  ["company_name", "Nazwa firmy"],
                  ["company_address", "Adres siedziby"],
                  ["nip", "NIP"],
                  ["contact_email", "Email kontaktowy"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-slate-400">{label}</label>
                    <input
                      data-testid={`settings-${key}`}
                      value={settings[key] || ""}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                      className="mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-200 outline-none transition-colors focus:border-cyan-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="rounded-2xl glass-panel p-6">
                <h3 className="font-display text-sm font-bold text-slate-200">Płatności — przelew i BLIK</h3>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-xs text-slate-400">Numer konta bankowego (przelewy)</label>
                    <input data-testid="settings-bank-account" value={settings.bank_account || ""} onChange={(e) => setSettings({ ...settings, bank_account: e.target.value })}
                      placeholder="PL00 0000 0000 0000 0000 0000 0000"
                      className="mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 font-mono text-sm text-slate-200 outline-none transition-colors focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Telefon do BLIKA (przelewy na telefon)</label>
                    <input data-testid="settings-blik-phone" value={settings.blik_phone || ""} onChange={(e) => setSettings({ ...settings, blik_phone: e.target.value })}
                      placeholder="600 000 000"
                      className="mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-200 outline-none transition-colors focus:border-cyan-500" />
                  </div>
                </div>
              </div>
              <div className="rounded-2xl glass-panel p-6">
                <h3 className="font-display text-sm font-bold text-slate-200">Stripe (karty, BLIK online)</h3>
                <div className="mt-4 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3.5 py-2.5">
                    <span className="text-slate-400">Tryb</span>
                    <span className="rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2.5 py-0.5 font-bold text-emerald-300">{settings.stripe_mode === "test" ? "TESTOWY" : "PRODUKCYJNY"}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3.5 py-2.5">
                    <span className="text-slate-400">Konto</span>
                    <span className="font-mono text-slate-300">{settings.stripe_account_id}</span>
                  </div>
                  <p className="pt-1 text-[11px] leading-relaxed text-slate-500">
                    Katalog cenowy jest synchronizowany automatycznie z produktami sklepu. Aby przejść na płatności produkcyjne, aktywuj konto Stripe (link otrzymałeś osobno) — klucze podmienią się automatycznie przy wdrożeniu.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <button
                data-testid="settings-save-button"
                onClick={saveSettings}
                disabled={saving}
                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 px-8 py-3 font-display text-sm font-bold text-slate-950 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                <Save className="h-4 w-4" /> {saving ? "Zapisywanie..." : "Zapisz ustawienia"}
              </button>
            </div>
          </div>
        )}
      </div>

      {emailPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" onClick={() => setEmailPreview(null)} data-testid="email-preview-modal">
          <div className="w-full max-w-3xl rounded-2xl border border-cyan-500/20 bg-slate-900 p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Podgląd e-maila z produktem (tryb demo)</h3>
              <button onClick={() => setEmailPreview(null)} data-testid="email-preview-close" className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-cyan-500/50">Zamknij</button>
            </div>
            <iframe title="Podgląd e-maila" srcDoc={emailPreview} className="h-[70vh] w-full rounded-xl bg-white" sandbox="" />
          </div>
        </div>
      )}
    </main>
  );
}
